import { Injectable, PLATFORM_ID, inject, Injector } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { ConsentService } from '../../consent/consent.service';

/**
 * Native-only splash choreography.
 *
 * The native launch screen (plain white) is kept visible via `launchAutoHide: false`
 * until the webview has painted the `#app-splash` overlay from index.html. We then
 * hide the native splash (white → white, invisible swap), let the in-app logo
 * fade in and pulse exactly twice (CSS-driven, see index.html). Once the pulses
 * are done we cross-fade the overlay away, regardless of app state.
 * Long enough to register, short enough not to annoy.
 */
@Injectable({ providedIn: 'root' })
export class SplashService {
  /** Fallback if the pulse animation never reports back (e.g. reduced motion). */
  private static readonly MAX_PULSE_WAIT_MS = 1800;
  /** Keep in sync with the `#app-splash.is-hiding` transition in index.html. */
  private static readonly FADE_MS = 650;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);

  initialize(): void {
    if (!isPlatformBrowser(this.platformId) || !Capacitor.isNativePlatform()) {
      return;
    }
    void this.run();
  }

  private async run(): Promise<void> {
    const overlay = document.getElementById('app-splash');

    // Wait one frame so the overlay is definitely painted before the native splash goes.
    await nextFrame();

    try {
      await SplashScreen.hide({ fadeOutDuration: 0 });
    } catch {
      // Plugin unavailable — nothing to hide.
    }

    if (overlay) {
      await Promise.race([
        animationEnd(overlay, 'app-splash-pulse'),
        delay(SplashService.MAX_PULSE_WAIT_MS),
      ]);

      overlay.classList.add('is-hiding');
      await delay(SplashService.FADE_MS);
      overlay.remove();
      document.documentElement.classList.remove('has-app-splash');
    }

    // ATT after splash so the system dialog is not covered by the overlay.
    if (Capacitor.getPlatform() === 'ios') {
      await this.injector.get(ConsentService).syncFromAtt({ requestIfNeeded: true });
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function animationEnd(el: Element, animationName: string): Promise<void> {
  return new Promise((resolve) => {
    el.addEventListener('animationend', (e) => {
      if ((e as AnimationEvent).animationName === animationName) {
        resolve();
      }
    });
  });
}
